import { Box, Stack, Typography } from '@mui/material';
import { KeyboardEvent } from 'react';

import { Switch } from '@/components/form/baseInputs/Switch';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { COLORS } from '@/styles/tokens/colors';

interface EnableSmartReviewCardProps {
  isEnabled: boolean;
  onChange: (enabled: boolean) => void;
}

/**
 * The "Enable SmartReview" toggle card. The whole card is the control (clicking
 * anywhere toggles it, per the design), so it carries the switch semantics; the
 * inner Switch is decorative and reflects state. Accented (blue) when enabled,
 * plain with a hover state when disabled.
 */
export function EnableSmartReviewCard({
  isEnabled,
  onChange,
}: EnableSmartReviewCardProps) {
  const toggle = () => onChange(!isEnabled);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <Box
      role="switch"
      aria-checked={isEnabled}
      aria-label="Enable SmartReview"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={handleKeyDown}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        p: 3,
        borderRadius: 1,
        border: '1px solid',
        cursor: 'pointer',
        ...(isEnabled
          ? { bgcolor: COLORS.BLUE[50], borderColor: COLORS.BLUE[300] }
          : {
              bgcolor: COLORS.PRIMITIVES.WHITE,
              borderColor: COLORS.GRAY[200],
              '&:hover': { bgcolor: COLORS.GRAY[50] },
            }),
      }}
    >
      {/* Decorative — the card itself is the accessible switch control. */}
      <Box aria-hidden sx={{ pointerEvents: 'none', display: 'flex' }}>
        <Switch
          label=""
          value={isEnabled}
          onChange={toggle}
          name="enableSmartReview"
        />
      </Box>
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="title-s">Enable SmartReview</Typography>
          <Badge variant={BadgeVariants.Accent} display="Beta" />
        </Stack>
        <Typography variant="body-s">
          High-confidence AI suggestions are approved automatically. You can
          review and edit all data at any point.
        </Typography>
      </Stack>
    </Box>
  );
}
